import type { useSnackbar } from '@applied/dragonfruit/src/components/PageSnackbar/snackbar_provider';
import type { useQueryClient } from '@tanstack/react-query';
import { CandidateExperienceQueryKeys } from 'api/query_keys';
import isEqual from 'lodash/isEqual';

/**
 * Given the original value of the field and the updated value, optimistically update
 * the field and revert the state if the request fails. Ensure the cache gets updated
 * if the request succeeds.
 *
 * postUpdatedState is expected to throw an error if the request fails.
 *
 * onSuccess generally invalidates cache keys. You must handle errors within the function;
 * don't raise any errors.
 * onError generally reports the error appropriately.
 */
export async function optimisticFieldUpdate<T>(
  originalValue: T,
  updatedValue: T,
  updateLocalState: (updatedValue: T) => void,
  postUpdatedState: (updatedValue: T) => Promise<void>,
  onSuccess: () => Promise<void>,
  onError: (error: Error) => void,
): Promise<void> {
  if (isEqual(originalValue, updatedValue)) {
    return;
  }

  // Optimistically update the local state.
  updateLocalState(updatedValue);

  // Post the update to the backend.
  try {
    await postUpdatedState(updatedValue);
  } catch (error) {
    // Revert the local state to the original value.
    updateLocalState(originalValue);
    onError(error);
    return;
  }

  // Invalidate query keys so that the new value gets cached.
  await onSuccess();
}

/**
 * Displays a message to the snackbar and logs the error to the console.
 */
export function showAndLogError(
  snackbar: ReturnType<typeof useSnackbar>,
  message: string,
  error: Error,
): void {
  snackbar.create(message, 'OK');
  console.error(message, error);
}

/**
 * Invalidates the application page cache and tasks page cache so that any visualization
 * of the application is up-to-date.
 *
 * TODO: Should probably have more granular requests/invalidation.
 */
export async function invalidateQueryKeys(
  queryClient: ReturnType<typeof useQueryClient>,
): Promise<void> {
  // Invalidate this page
  await queryClient.invalidateQueries([CandidateExperienceQueryKeys.WEB_OFFER]);
}

/**
 * Throw an error if the response body is falsy or failed.
 */
export function assertResponseSuccess(response: any): any {
  if (!response) {
    throw new Error('No response');
  }
  if (response.error) {
    throw new Error(`Request failed: ${response.error}`);
  }
  return response;
}
